import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { mockData } from '../data/mockData';
import { scrollToSection, contactWhatsApp, makePhoneCall, sendEmail, openGoogleMaps } from '../utils/buttonActions';
import { submitNetlifyForm } from '../utils/netlifyForms';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');

  const quickLinks = [
    { name: 'HOME', href: 'home' },
    { name: 'SERVICES', href: 'services' },
    { name: 'PORTFOLIO', href: 'portfolio' },
    { name: 'ABOUT', href: 'about' },
    { name: 'CONTACT', href: 'contact' }
  ];

  const services = [
    { name: 'FLEX PRINTING', href: 'services' },
    { name: 'VINYL GRAPHICS', href: 'services' },
    { name: 'FRAME WELDING', href: 'services' },
    { name: 'INSTALLATION', href: 'services' },
    { name: 'CUSTOM DESIGN', href: 'services' }
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribing(true);
    try {
      await submitNetlifyForm('newsletter', {
        email,
        source: 'footer'
      });

      setSubscriptionMessage('Subscribed successfully!');
      setTimeout(() => contactWhatsApp(`Aim Flex Newsletter Signup
Email: ${email}`), 1000);
      setEmail('');
      setTimeout(() => setSubscriptionMessage(''), 3000);
    } catch (error) {
      setSubscriptionMessage('Failed. Try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      {/* Big CTA Banner */}
      <div className="footer-command footer-divider border-b border-[#333] py-16 lg:py-20 text-center">
        <p className="text-[11px] font-black tracking-[0.22em] uppercase text-[#FF2A00] mb-4">Ready for production</p>
        <h2 className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter uppercase leading-none mb-6">
          Let's <span className="text-[#FF2A00]">Print</span>
        </h2>
        <button
          onClick={() => contactWhatsApp('Hi! I\'d like to discuss my project.')}
          className="industrial-btn industrial-btn-primary inline-flex"
          data-testid="footer-cta"
        >
          START A PROJECT
        </button>
      </div>

      {/* Main Footer Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="brand-logo-frame">
                <img src="/aim-logo.png" alt="Aim Flex" className="h-10 w-auto object-contain" />
              </div>
              <div>
                <h3 className="brand-wordmark text-base font-black tracking-tighter uppercase">{mockData.company.name}</h3>
                <p className="brand-tagline text-[10px] tracking-widest text-[#888]">THE PRINTING PEOPLE</p>
              </div>
            </div>
            <p className="footer-muted text-sm text-[#888] leading-relaxed">
              {mockData.company.tagline}. With {mockData.company.experience} of experience, delivering high-quality signage from concept to installation.
            </p>

            <div className="space-y-3">
              <button onClick={() => makePhoneCall()} className="footer-muted flex items-center space-x-3 text-sm text-[#888] hover:text-[#FF2A00] transition-colors w-full text-left" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-[#FF2A00]" strokeWidth={2} /><span>{mockData.contact.phone}</span>
              </button>
              <button onClick={() => sendEmail()} className="footer-muted flex items-center space-x-3 text-sm text-[#888] hover:text-[#FF2A00] transition-colors w-full text-left" data-testid="footer-email">
                <Mail className="h-4 w-4 text-[#FF2A00]" strokeWidth={2} /><span>{mockData.contact.email}</span>
              </button>
              <button onClick={() => openGoogleMaps()} className="footer-muted flex items-start space-x-3 text-sm text-[#888] hover:text-[#FF2A00] transition-colors w-full text-left" data-testid="footer-address">
                <MapPin className="h-4 w-4 text-[#FF2A00] mt-0.5" strokeWidth={2} /><span>{mockData.contact.fullAddress}</span>
              </button>
              <div className="footer-muted flex items-center space-x-3 text-sm text-[#888]">
                <Clock className="h-4 w-4 text-[#FF2A00]" strokeWidth={2} /><span>{mockData.contact.businessHours}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-bold tracking-widest mb-6">QUICK LINKS</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="footer-muted text-sm text-[#888] hover:text-[#FF2A00] transition-colors flex items-center group"
                    data-testid={`footer-link-${link.href}`}
                  >
                    <ArrowRight className="h-3 w-3 mr-2 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-bold tracking-widest mb-6">SERVICES</h4>
            <ul className="space-y-3">
              {services.map((service, i) => (
                <li key={i}>
                  <button
                    onClick={() => scrollToSection(service.href)}
                    className="footer-muted text-sm text-[#888] hover:text-[#FF2A00] transition-colors flex items-center group"
                  >
                    <ArrowRight className="h-3 w-3 mr-2 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h5 className="text-xs font-bold tracking-widest mb-3">HOURS</h5>
              <div className="footer-muted space-y-1 text-xs text-[#888]">
                <div className="flex justify-between"><span>Mon-Fri</span><span>9AM-7PM</span></div>
                <div className="flex justify-between"><span>Saturday</span><span>9AM-6PM</span></div>
                <div className="flex justify-between"><span>Sunday</span><span className="text-[#FF2A00]">Closed</span></div>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs font-bold tracking-widest mb-6">STAY UPDATED</h4>
            <p className="footer-muted text-sm text-[#888] mb-4">Subscribe for latest updates and special offers.</p>

            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com" required
                className="w-full p-3 bg-[#1a1a1a] border-2 border-[#333] text-white placeholder-[#666] focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit" disabled={isSubscribing}
                className="w-full bg-[#FF2A00] text-white py-3 text-xs font-bold tracking-widest uppercase border-2 border-[#FF2A00] hover:bg-white hover:text-[#0A0A0A] hover:border-white transition-all duration-200 disabled:opacity-50"
                data-testid="footer-newsletter-submit"
              >
                {isSubscribing ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
              </button>
              {subscriptionMessage && (
                <p className="text-xs text-center text-[#FF2A00]">{subscriptionMessage}</p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-divider border-t border-[#333]">
        <div className="footer-muted max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center text-xs text-[#666]">
          <p>2024 {mockData.company.name}. All rights reserved. Est. {mockData.company.established}</p>
          <div className="flex space-x-6 mt-2 md:mt-0">
            <button className="hover:text-[#FF2A00] transition-colors">Privacy</button>
            <button className="hover:text-[#FF2A00] transition-colors">Terms</button>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-[9999]">
        <button
          onClick={() => contactWhatsApp('Hi! I found your website and would like to know more.')}
          className="bg-[#25D366] text-white w-14 h-14 flex items-center justify-center border-2 border-[#0A0A0A] shadow-brutal hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 animate-pulse hover:animate-none"
          aria-label="WhatsApp"
          data-testid="whatsapp-cta"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.506z"/>
          </svg>
        </button>
      </div>
    </footer>
  );
};

export default Footer;
